import api, { ApiResponse } from './api';

// Devamsizlik Raporu
export interface AttendanceReportEmployee {
  employeeId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  employeeCode?: string;
  departmentName?: string;
  positionName?: string;
  workDays: number;
  absentDays: number;
  lateDays: number;
  totalLateMinutes: number;
  lateHours: number;
  earlyLeaveDays: number;
  totalEarlyLeaveMinutes: number;
  earlyLeaveHours: number;
  totalOvertimeMinutes: number;
  overtimeHours: number;
  totalWorkMinutes: number;
  totalWorkHours: number;
  attendanceRate: number;
}

export interface AttendanceReport {
  startDate: string;
  endDate: string;
  totalWorkDays: number;
  employees: AttendanceReportEmployee[];
}

// Fazla Mesai Raporu
export interface OvertimeReportEmployee {
  employeeId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  employeeCode?: string;
  departmentName?: string;
  workDays: number;
  totalOvertimeMinutes: number;
  overtimeHours: number;
}

export interface OvertimeReport {
  startDate: string;
  endDate: string;
  totalOvertimeMinutes: number;
  totalOvertimeHours: number;
  employees: OvertimeReportEmployee[];
}

// Gec Kalma Raporu
export interface LateReportEmployee {
  employeeId: number;
  firstName: string;
  lastName: string;
  fullName: string;
  employeeCode?: string;
  departmentName?: string;
  lateDays: number;
  totalLateMinutes: number;
  lateHours: number;
  maxLateMinutes?: number;
  avgLateMinutes: number;
}

export interface LateReport {
  startDate: string;
  endDate: string;
  totalLateDays: number;
  totalLateMinutes: number;
  totalLateHours: number;
  employees: LateReportEmployee[];
}

// Departman Ozet Raporu
export interface DepartmentSummary {
  departmentID: number;
  departmentName: string;
  employeeCount: number;
  totalWorkDays: number;
  totalWorkMinutes: number;
  totalWorkHours: number;
  totalOvertimeMinutes: number;
  overtimeHours: number;
  totalLateMinutes: number;
  lateHours: number;
  lateDaysCount: number;
}

export interface DepartmentSummaryReport {
  startDate: string;
  endDate: string;
  departments: DepartmentSummary[];
}

// Dashboard Istatistikleri
export interface ReportStats {
  today: {
    date: string;
    totalEmployees: number;
    presentCount: number;
    absentCount: number;
    lateCount: number;
    overtimeMinutes: number;
  };
  thisMonth: {
    startDate: string;
    endDate: string;
    totalWorkMinutes: number;
    totalWorkHours: number;
    totalOvertimeMinutes: number;
    totalOvertimeHours: number;
    totalLateMinutes: number;
    totalLateHours: number;
  };
}

// Filtre parametreleri
export interface ReportFilters {
  startDate?: string;
  endDate?: string;
  departmentId?: number;
  employeeId?: number;
  siteId?: number;
  locationId?: number;
}

// Ortak site+location param helper
const appendSiteLocation = (params: URLSearchParams, filters?: ReportFilters) => {
  if (filters?.siteId) params.append('siteId', filters.siteId.toString());
  if (filters?.locationId) params.append('locationId', filters.locationId.toString());
};

const reportsService = {
  // Devamsizlik raporu
  getAttendanceReport: async (filters?: ReportFilters): Promise<ApiResponse<AttendanceReport>> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.departmentId) params.append('departmentId', filters.departmentId.toString());
    if (filters?.employeeId) params.append('employeeId', filters.employeeId.toString());
    appendSiteLocation(params, filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/reports/attendance${query}`);
  },

  // Fazla mesai raporu
  getOvertimeReport: async (filters?: ReportFilters): Promise<ApiResponse<OvertimeReport>> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.departmentId) params.append('departmentId', filters.departmentId.toString());
    appendSiteLocation(params, filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/reports/overtime${query}`);
  },

  // Gec kalma raporu
  getLateReport: async (filters?: ReportFilters): Promise<ApiResponse<LateReport>> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    if (filters?.departmentId) params.append('departmentId', filters.departmentId.toString());
    appendSiteLocation(params, filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/reports/late${query}`);
  },

  // Departman ozet raporu
  getDepartmentSummary: async (filters?: ReportFilters): Promise<ApiResponse<DepartmentSummaryReport>> => {
    const params = new URLSearchParams();
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);
    appendSiteLocation(params, filters);
    const query = params.toString() ? `?${params.toString()}` : '';
    return api.get(`/reports/department-summary${query}`);
  },

  // Dashboard istatistikleri
  getStats: async (): Promise<ApiResponse<ReportStats>> => {
    return api.get('/reports/stats');
  },
};

export default reportsService;
